import { useState, useMemo, useEffect } from "react";
import { ProductImageWithFallbacks } from "./ProductImageWithFallbacks";
import DriveVideoPlayer from "./DriveVideoPlayer";
import { getVideoMimeTypeForUrl } from "../utils/parseProductMedia";

function normalizeImageGroups(product) {
  if (product?.imageGroups?.length) return product.imageGroups;
  if (product?.images?.length) return [product.images];
  return [];
}

function buildGalleryItems(product) {
  if (product?.galleryItems?.length) return product.galleryItems;
  return normalizeImageGroups(product).map((urls) => ({ kind: "image", urls }));
}

function ProductMediaGallery({ product }) {
  const galleryItems = useMemo(() => buildGalleryItems(product), [product]);
  /** Legacy: videos merged into galleryItems from sheet; kept for older cached product objects */
  const legacyVideos = product?.videos || [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoomStyle, setZoomStyle] = useState({});

  useEffect(() => {
    setActiveIndex(0);
    setZoomStyle({ transform: "scale(1)" });
  }, [product?.id]);

  useEffect(() => {
    setZoomStyle({ transform: "scale(1)" });
  }, [activeIndex]);

  const activeSlide = galleryItems[Math.min(activeIndex, Math.max(0, galleryItems.length - 1))];
  const isImageSlide = activeSlide && activeSlide.kind === "image";
  const isEmbedSlide = activeSlide && activeSlide.kind === "embed";
  const hasMultiple = galleryItems.length > 1;

  const handleMouseMove = (e) => {
    if (!isImageSlide) return;
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.pageX - left) / width) * 100;
    const y = ((e.pageY - top) / height) * 100;
    setZoomStyle({
      transformOrigin: `${x}% ${y}%`,
      transform: "scale(2)",
    });
  };

  const handleMouseLeave = () => {
    setZoomStyle({ transform: "scale(1)" });
  };

  const renderEmbed = (slide, className, title) => {
    if (!slide || slide.kind !== "embed") return null;
    if (slide.provider === "file") {
      return (
        <video controls playsInline className={className} style={{ objectFit: "contain", background: "#111" }}>
          <source src={slide.embedUrl} type={getVideoMimeTypeForUrl(slide.embedUrl)} />
        </video>
      );
    }
    if (slide.provider === "drive" && (slide.streamUrls?.length || slide.streamUrl)) {
      return (
        <DriveVideoPlayer
          previewUrl={slide.embedUrl}
          streamUrls={slide.streamUrls}
          streamUrl={slide.streamUrl}
          streamUrlAlt={slide.streamUrlAlt}
          className={className}
          title={title}
        />
      );
    }
    return (
      <iframe
        src={slide.embedUrl}
        title={title}
        className={`${className} border-0`}
        style={{ minHeight: "280px", width: "100%" }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
        allowFullScreen
      />
    );
  };

  return (
    <div className="product-media-gallery">
      {galleryItems.length > 0 && (
        <div className={`product-media-gallery__row${hasMultiple ? " product-media-gallery__row--with-thumbs" : ""}`}>
          {hasMultiple && (
            <div className="product-media-gallery__thumbs-col" role="tablist" aria-label="Product photos and videos">
              {galleryItems.map((slide, idx) => (
                <button
                  key={idx}
                  type="button"
                  role="tab"
                  aria-selected={activeIndex === idx}
                  className={`p-0 border rounded overflow-hidden bg-white product-media-gallery__thumb ${activeIndex === idx ? "border-primary border-2" : "border-secondary"}`}
                  onClick={() => setActiveIndex(idx)}
                >
                  {slide.kind === "embed" ? (
                    <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-dark text-white small product-media-gallery__thumb-embed">
                      <span aria-hidden>▶</span>
                    </div>
                  ) : (
                    <ProductImageWithFallbacks
                      urls={slide.urls}
                      alt={`${product.name} — ${idx + 1}`}
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                      loading="lazy"
                    />
                  )}
                </button>
              ))}
            </div>
          )}
          <div
            className={`product-media-gallery__main-col border product-media-gallery__main ${isEmbedSlide ? "product-media-gallery__main--embed bg-dark" : "overflow-hidden bg-white"}`}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={
              isEmbedSlide
                ? { minHeight: "320px", maxHeight: "min(85vh, 720px)" }
                : { minHeight: "320px", maxHeight: "560px", aspectRatio: "3 / 4" }
            }
          >
            {activeSlide?.kind === "embed" ? (
              <div className="product-media-gallery__embed-shell w-100 h-100 d-flex flex-column">
                {renderEmbed(activeSlide, "w-100 flex-grow-1", `${product.name} — video`)}
              </div>
            ) : (
              <ProductImageWithFallbacks
                urls={activeSlide?.urls || []}
                alt={product.name}
                className="img-fluid w-100 h-100"
                style={{ objectFit: "contain", objectPosition: "center center", ...zoomStyle }}
              />
            )}
          </div>
        </div>
      )}

      {galleryItems.length === 0 && legacyVideos.length === 0 && (
        <div
          className="overflow-hidden border product-media-gallery__main bg-white"
          style={{ minHeight: "320px", maxHeight: "560px", aspectRatio: "3 / 4" }}
        >
          <ProductImageWithFallbacks
            urls={[]}
            alt={product.name}
            className="img-fluid w-100 h-100"
            style={{ objectFit: "contain", objectPosition: "center center" }}
          />
        </div>
      )}

      {legacyVideos.length > 0 && (
        <div className={galleryItems.length ? "mt-4" : ""}>
          <h3 className="h6 mb-2">Videos</h3>
          <div className="d-flex flex-column gap-3">
            {legacyVideos.map((v, i) => (
              <div key={`${v.originalUrl}-${i}`} className="ratio ratio-16x9 border rounded overflow-hidden bg-dark">
                {v.provider === "file" ? (
                  <video controls playsInline className="w-100 h-100" style={{ objectFit: "contain" }}>
                    <source src={v.embedUrl} type={getVideoMimeTypeForUrl(v.embedUrl)} />
                    Your browser does not support this video.
                  </video>
                ) : v.provider === "drive" && (v.streamUrls?.length || v.streamUrl) ? (
                  <DriveVideoPlayer
                    previewUrl={v.embedUrl}
                    streamUrls={v.streamUrls}
                    streamUrl={v.streamUrl}
                    streamUrlAlt={v.streamUrlAlt}
                    className="w-100 h-100"
                    title={`Product video ${i + 1}`}
                  />
                ) : (
                  <iframe
                    src={v.embedUrl}
                    title={`Product video ${i + 1}`}
                    className="w-100 h-100 border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductMediaGallery;
